<template lang="pug">
#waiter-register(v-cloak)
  .box
    .box-header.with-border
      .d-flex.justify-content-start.align-items-center
        .d-flex.justify-content-start.flex-column
          .title
            span 入所申込者の新規登録
        .d-flex.justify-content-end.align-items-start.align-self-stretch.flex-fill
          button.btn.add-btn(type="button" @click="onAddWaiter"
            :disabled="!existApplicationTemplateId || !existRoomType"
            :class="{ 'disable': !existApplicationTemplateId || !existRoomType }") 申込み者用ページ

    .box-body.register-body
      .message-container(v-if="!existApplicationTemplateId")
        .warning-message
          div 入所申込み様式が設定されていないため、登録できません。
          div 設定をご確認ください。
      .message-container(v-if="!existRoomType")
        .warning-message
          div 本施設、グループ施設に申込可能な部屋が設定されていないため、登録できません。
          div 設定をご確認ください。

      .template-container(v-if="template && existRoomType")
        .information.mb-2(v-if="template.information" style="white-space: pre-wrap; word-wrap: break-word;") {{ template.information }}

        .template(v-for="(property, index) in template.template.properties")
          WaiterTemplateInputItem(v-model="template.template.properties[index]" :waiterViewableFacilitiesMap="waiterViewableFacilitiesMap" :isChaild="false")

        .created
          .d-flex.flex-row.align-items-center
            .title.mr-1 申込日
            span.validation-label.mr-1 必須
          .d-flex.flex-row.align-items-center.p-3
            b-form-select.year(:id="'created-year'" v-model="admissionApplicationCreated.year" :name="'created-year'"
              :options="getYearOptions(admissionApplicationCreated)"
              :class="[ { noinput: !admissionApplicationCreated.year } ]" v-validate="{ required: true }" data-vv-as="年")
            span.ml-2.mr-2 年
            b-form-select.month(:id="'created-month'" v-model="admissionApplicationCreated.month" :name="'created-month'" :options="getMonthOptions()"
              :class="[ { noinput: !admissionApplicationCreated.month } ]" v-validate="{ required: true }" data-vv-as="月")
            span.ml-2.mr-2 月
            b-form-select.date(:id="'created-date'" v-model="admissionApplicationCreated.date" :name="'created-date'" :options="getDateOptions()"
              :class="[ { noinput: !admissionApplicationCreated.date } ]" v-validate="{ required: true }" data-vv-as="日")
            span.ml-2.mr-2 日
          .error-messages(v-for="error in errors.collect('created-year')")
            .error-message {{ error }}
          .error-messages(v-for="error in errors.collect('created-month')")
            .error-message {{ error }}
          .error-messages(v-for="error in errors.collect('created-date')")
            .error-message {{ error }}
          .error-messages(v-for="error in errors.collect('created')")
            .error-message {{ error }}

        .labels(v-if="_showLabels")
          .title ラベル
          .inputs.mb-3.ml-3(v-for="label in labels")
            b-form-checkbox(v-if="label.use" :id="'label-' + label.id" v-model="label.checked" :name="'label-' + label.id" :value="true" :unchecked-value="false")
              span.waiter-label.mr-2(:style="{ 'color': label.color, 'background-color': label.bgColor }") {{ label.name }}
              span {{ label.description }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.register-btn(v-if="existRoomType && existApplicationTemplateId" @click="showConfirmModal(8)" type="button") 一時保存
        button.btn.register-btn(v-if="existRoomType && existApplicationTemplateId" @click="showConfirmModal(10)" type="button") 登録
        button.btn.cancel-btn(@click="gotoWaiterList" type="button") キャンセル

  OkCancelModal(v-model="showRegisterModal" :title="registerModalTitle" @ok="registerWaiter")
  OkModal(v-model="showRegisterSuccessModal" :title="successModalTitle" @ok="gotoWaiterList")

  //- file upload error modal
  FileUploadErrorModal(v-model="showUploadErrorModal")
  
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
  ProgressLoading(v-model="currentUploadCount" :max="uploadCount" :isBusy="progressLoading" :message="progressMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore, screenInputTemporaryStore } from "@/store/store";
import { needRefreshesStore } from "@/store/store";
import { WaiterListState, SendWaiterMessagesState } from "@/store/NeedRefreshes";
import { PcUrl } from "@/commons/constants/PcUrl";

import CustomValidate from "@/commons/utils/CustomValidate";
import LoginUser from "@/store/LoginUser";

import TemplatesService, {
  AdmissionApplicationTemplatesGetModel,
  RoomType,
  TemplateItem,
  TemplateItemBirthday,
  TemplateItemFile,
  TemplateItemGroup,
  TemplateItemInput,
  TemplateItemRadioGroup,
  TemplateItemRoomType,
  TemplateItemSection,
  TemplateItemSwitchCheckbox,
  TemplateType,
  YearMonthDateAnswer
} from "@/commons/services/AdmissionApplicationTemplates";
import FacilitiesService, { CustomRecruitmentRoom, FacilityGetModel } from "@/commons/services/Facilities";
import WaitersService, { WaitersPostModel } from "@/commons/services/Waiters";
import S3LocationsService from "@/commons/services/S3Locations";
import { S3LocationRequest, S3LocationResponse, S3LocationType } from "@/commons/models/S3Location";
import S3UploadsService from "@/commons/services/S3Uploads";
import WaiterLabelsService, { WaiterLabelsGetModel } from "@/commons/services/WaiterLabels";

import FileUploadErrorModal from "@/pc/basics/FileUploadErrorModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ProgressLoading from "@/pc/basics/ProgressLoading.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import WaiterTemplateInputItem from "@/pc/containers/waiters/WaiterTemplateInputItem.vue";
import { ResidentStatus } from "@/commons/services/Residents";

/**
 * ### 待機者登録画面コンポーネント
 */
@Component({
  components: {
    FileUploadErrorModal,
    OkCancelModal,
    OkModal,
    PageLoading,
    ProgressLoading,
    WaiterTemplateInputItem,
    ValidationAlert
  },
  $_veeValidate: { validator: "new" }
})
export default class WaiterRegister extends Vue {
  /** 入所申込テンプレートサービス */
  @Inject("AdmissionApplicationTemplatesService") templatesService!: TemplatesService;
  /** 施設サービス */
  @Inject("FacilitiesService") facilitiesService!: FacilitiesService;
  /** 待機者サービス */
  @Inject("WaitersService") waitersService!: WaitersService;
  /** S3格納先URLサービス */
  @Inject("S3LocationsService") s3LocationsService!: S3LocationsService;
  /** S3ファイルアップロードサービス */
  @Inject("S3UploadsService") s3UploadsService!: S3UploadsService;
  /** 待機者ラベルサービス */
  @Inject("WaiterLabelsService") waiterLabelsService!: WaiterLabelsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 登録確認モーダル表示可否 */
  showRegisterModal: boolean = false;
  /** 登録確認モーダルタイトル */
  registerModalTitle: string = "";
  /** 登録完了モーダル表示可否 */
  showRegisterSuccessModal: boolean = false;
  /** 登録完了モーダルタイトル */
  successModalTitle: string = "";

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 入居申込テンプレート情報が設定されているか否か */
  existApplicationTemplateId: boolean = true;
  /** 部屋種類が存在するか否か */
  existRoomType: boolean = true;

  /** 現在のファイルアップロードファイル数 */
  currentUploadCount: number = 0;
  /** ファイルアップロードファイル数(母数) */
  uploadCount: number = 0;
  /** プログレスローディングのメッセージ */
  progressMessage: string = "";
  /** プログレスローディングの表示可否 */
  progressLoading: boolean = false;

  /** 閲覧可能施設のMAP、key：施設ID、value：施設情報 */
  waiterViewableFacilitiesMap: Map<number, FacilityGetModel> = new Map<number, FacilityGetModel>();
  /** 入居申込テンプレート */
  template: AdmissionApplicationTemplatesGetModel | null = null;
  /** 利用者状態 */
  residentStatus: ResidentStatus = ResidentStatus.UNKNOWN;
  /** 申込日（プロパティ名はcreatedだが問題無し）reisident.waiter.admissionApplication.createdの値を指す */
  admissionApplicationCreated: YearMonthDateAnswer = {
    year: "",
    month: "",
    date: ""
  };
  /** ラベル */
  labels: Array<WaiterLabel> = [];

  /** 確認モーダル用エラーが存在するか */
  validInputError: boolean = true;
  /** ファイルアップロードエラーモーダル表示可否 */
  showUploadErrorModal: boolean = false;

  get _showLabels(): boolean {
    for (const label of this.labels) {
      if (label.use) {
        return true;
      }
    }
    return false;
  }

  /**
   * ### vue lifecycle created
   */
  public created(): void {
    CustomValidate.addCustomValidate(this.$validator);
    return;
  }

  /**
   * ### vue lifecycle created
   *
   * - 入居申込テンプレートが設定されていない場合はメッセージを表示して終了する
   * - 上記以外は画面表示情報を作成する
   */
  public async mounted(): Promise<void> {
    if (userStore.facility.useAdmissionApplicationTemplateId == null) {
      this.existApplicationTemplateId = false;
      return;
    }

    await this.createView();

    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const waiterViewableFacilities: Array<FacilityGetModel> = await this.getWaiterViewableFacilities(userStore);
    const waiterViewableFacilitiesMap: Map<number, FacilityGetModel> = this.createWaiterViewableFacilitiesMap(waiterViewableFacilities);
    this.waiterViewableFacilitiesMap = waiterViewableFacilitiesMap;

    this.existRoomType = this.getExistRoomType(userStore.facility, waiterViewableFacilities);

    const template: AdmissionApplicationTemplatesGetModel = await this.getTemplate();
    this.setRoomTypeAnswer(template.template.properties, waiterViewableFacilities);
    this.initializeFileItems(template.template.properties);
    this.template = template;

    const now: Date = new Date();
    this.admissionApplicationCreated.year = this.$dateUtil.convertDateToString(now, "yyyy", false);
    this.admissionApplicationCreated.month = this.$dateUtil.convertDateToString(now, "mm", false);
    this.admissionApplicationCreated.date = this.$dateUtil.convertDateToString(now, "dd", false);

    const labels: Array<WaiterLabel> = await this.getWaiterLabels();
    this.labels = labels;

    // storeに一時保存した入力内容を復元する
    this.restoreScreenInputTemporaryStore();

    this.isBusy = false;

    return;
  }

  /**
   * ### 閲覧可能施設情報を取得する
   */
  public async getWaiterViewableFacilities(userStore: LoginUser): Promise<Array<FacilityGetModel>> {
    const waiterViewableFacilities: Array<FacilityGetModel> = [];
    for (const facilityId of userStore.facility.waiterViewableFacilities) {
      const response: FacilityGetModel = await this.facilitiesService.get(userStore, { targetFacilityId: facilityId });
      waiterViewableFacilities.push(response);
    }
    return waiterViewableFacilities;
  }

  /**
   * ### 閲覧可能施設情報のMAPを作成する
   */
  public createWaiterViewableFacilitiesMap(waiterViewableFacilities: Array<FacilityGetModel>): Map<number, FacilityGetModel> {
    const waiterViewableFacilitiesMap: Map<number, FacilityGetModel> = new Map<number, FacilityGetModel>();
    waiterViewableFacilitiesMap.set(userStore.facility.id, userStore.facility);
    for (const facility of waiterViewableFacilities) {
      waiterViewableFacilitiesMap.set(facility.id, facility);
    }
    return waiterViewableFacilitiesMap;
  }

  /**
   * ### 部屋種類が存在するかを返す
   */
  public getExistRoomType(mineFacility: FacilityGetModel, waiterViewableFacilities: Array<FacilityGetModel>): boolean {
    const mineFacilityExistRoomType: boolean = this.isRecruitmentRoom(mineFacility) || this.isCustomRequiredRecruitmentRoom(mineFacility);

    let waiterViewableFacilitiesEmptyRoomType: boolean = false;
    for (const viewableFacility of waiterViewableFacilities) {
      const existRoomType: boolean = this.isRecruitmentRoom(viewableFacility) || this.isCustomRequiredRecruitmentRoom(viewableFacility);
      waiterViewableFacilitiesEmptyRoomType = waiterViewableFacilitiesEmptyRoomType || existRoomType;
    }

    return mineFacilityExistRoomType || waiterViewableFacilitiesEmptyRoomType;
  }

  public isRecruitmentRoom(facility: FacilityGetModel): boolean {
    if (
      (facility.recruitmentRoom.multiBed ||
        facility.recruitmentRoom.conventinalSingle ||
        facility.recruitmentRoom.unitTypeSingle ||
        facility.recruitmentRoom.double ||
        facility.recruitmentRoom.single) == true
    ) {
      return true;
    } else {
      return false;
    }
  }

  public isCustomRequiredRecruitmentRoom(facility: FacilityGetModel): boolean {
    if (!facility.customRecruitmentRoom) {
      return false;
    }
    for (const room of facility.customRecruitmentRoom) {
      if (room.use == true) {
        return true;
      }
    }
    return false;
  }

  /**
   * ### テンプレート情報を取得する
   */
  public async getTemplate(): Promise<AdmissionApplicationTemplatesGetModel> {
    const response: AdmissionApplicationTemplatesGetModel = await this.templatesService.get(userStore);
    return response;
  }

  /**
   * ### 部屋種類のanswer情報を格納する
   *
   * - 再帰呼び出しのため参照渡しする
   */
  public setRoomTypeAnswer(properties: Array<TemplateItem>, waiterViewableFacilities: Array<FacilityGetModel>): void {
    for (const property of properties) {
      if (this.hasProperties(property) == true) {
        if (property.type == TemplateType.SECTION) {
          const item: TemplateItemSection = property as TemplateItemSection;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            this.setRoomTypeAnswer(item.properties, waiterViewableFacilities);
          }
        } else if (property.type == TemplateType.GROUP) {
          const item: TemplateItemGroup = property as TemplateItemGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties)) {
                this.setRoomTypeAnswer(childProperties, waiterViewableFacilities);
              }
            }
          }
        } else if (property.type == TemplateType.RADIO_GROUP) {
          const item: TemplateItemRadioGroup = property as TemplateItemRadioGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties.properties)) {
                this.setRoomTypeAnswer(childProperties.properties, waiterViewableFacilities);
              }
            }
          }
        } else if (property.type == TemplateType.SWITCH_CHECKBOX) {
          const item: TemplateItemSwitchCheckbox = property as TemplateItemSwitchCheckbox;

          // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
          if (item.properties && Array.isArray(item.properties)) {
            this.setRoomTypeAnswer(item.properties, waiterViewableFacilities);
          }
        }
      }

      const inputItem: TemplateItemInput = property as TemplateItemInput;
      if (inputItem.type == TemplateType.ROOM_TYPE) {
        let roomTypeItem: TemplateItemRoomType = inputItem as TemplateItemRoomType;
        roomTypeItem.answer = this.createRoomTypeCheckbox(roomTypeItem, waiterViewableFacilities);
      }
    }

    return;
  }

  /**
   * ### ファイルの初期情報を作成する
   *
   * - 再帰呼び出しのため参照渡しする
   * - ファイルのanswerが空配列の場合、空のファイルを1つ追加する
   */
  public initializeFileItems(properties: Array<TemplateItem>): void {
    for (const property of properties) {
      if (this.hasProperties(property) == true) {
        if (property.type == TemplateType.SECTION) {
          const item: TemplateItemSection = property as TemplateItemSection;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            this.initializeFileItems(item.properties);
          }
        } else if (property.type == TemplateType.GROUP) {
          const item: TemplateItemGroup = property as TemplateItemGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties)) {
                this.initializeFileItems(childProperties);
              }
            }
          }
        } else if (property.type == TemplateType.RADIO_GROUP) {
          const item: TemplateItemRadioGroup = property as TemplateItemRadioGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties.properties)) {
                this.initializeFileItems(childProperties.properties);
              }
            }
          }
        } else if (property.type == TemplateType.SWITCH_CHECKBOX) {
          const item: TemplateItemSwitchCheckbox = property as TemplateItemSwitchCheckbox;

          // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
          if (item.properties && Array.isArray(item.properties)) {
            this.initializeFileItems(item.properties);
          }
        }
      }

      const inputItem: TemplateItemInput = property as TemplateItemInput;
      if (inputItem.type == TemplateType.FILE) {
        const fileItem: TemplateItemFile = inputItem as TemplateItemFile;

        if (fileItem.tempAnswer == null) {
          fileItem.tempAnswer = [];
        }

        if (fileItem.tempAnswer.length <= 0) {
          fileItem.tempAnswer.push({
            file: null as any,
            fileName: ""
          });
        }
      }
    }
  }

  /**
   * ### 部屋種類の初期値を作成する
   *
   * - 部屋種類(固有)の作成を行う
   *   - 自施設、閲覧可能施設の両方を設定対象とする
   * - 施設に部屋種類(固有)が存在する場合、部屋種類(固有)を作り直して設定する
   *   - 既存入力されている部屋種類(固有)をMAP化する
   *   - 施設の部屋種類(固有)の設定情報をもとに、MAPから前回入力値を復元する
   * - 施設に部屋種類(固有)が存在しない場合、部屋種類(固有)は空配列を設定する
   *
   * - 部屋種類(共通)の作成を行う
   *   - 自施設、閲覧可能施設の両方を設定対象とする
   * - 施設の部屋種類の情報をコピーする
   */
  public createRoomTypeCheckbox(item: TemplateItemRoomType, waiterViewableFacilities: Array<FacilityGetModel>): Array<RoomType> {
    let answer: Array<RoomType> = [];

    let customRecruitmentRoom: Array<CustomRecruitmentRoom> = [];
    if (userStore.facility.customRecruitmentRoom) {
      for (const room of userStore.facility.customRecruitmentRoom) {
        customRecruitmentRoom.push({
          id: room.id,
          name: room.name,
          abbreviationName: room.abbreviationName,
          color: room.color,
          use: false
        });
      }
    }

    const mineFacilityRoomTypeCheckbox: RoomType = {
      facilityId: userStore.facility.id,
      recruitmentRoom: {
        multiBed: false,
        conventinalSingle: false,
        unitTypeSingle: false,
        double: false,
        single: false
      },
      customRecruitmentRoom: customRecruitmentRoom
    };
    answer.push(mineFacilityRoomTypeCheckbox);

    for (const viewableFacility of waiterViewableFacilities) {
      // 部屋設定が全てfalseの場合は画面に表示する必要が無いのでスキップ
      if (!this.isRecruitmentRoom(viewableFacility) && !this.isCustomRequiredRecruitmentRoom(viewableFacility)) {
        continue;
      }

      let customRecruitmentRoom: Array<CustomRecruitmentRoom> = [];
      if (viewableFacility.customRecruitmentRoom) {
        for (const room of viewableFacility.customRecruitmentRoom) {
          customRecruitmentRoom.push({
            id: room.id,
            name: room.name,
            abbreviationName: room.abbreviationName,
            color: room.color,
            use: false
          });
        }
      }

      const roomTypeCheckbox: RoomType = {
        facilityId: viewableFacility.id,
        recruitmentRoom: {
          multiBed: false,
          conventinalSingle: false,
          unitTypeSingle: false,
          double: false,
          single: false
        },
        customRecruitmentRoom: customRecruitmentRoom
      };
      answer.push(roomTypeCheckbox);
    }

    item.answer = answer;

    return item.answer;
  }

  public getYearOptions(): Array<{ text: string; value: string | null }> {
    const options: Array<{ text: string; value: string | null }> = [{ text: "", value: null }]; // 初期値にリセット用の選択肢を用意しておく

    const currentYear: number = this.$dateUtil.convertDateToNumber(new Date(), "yyyy");
    const startPosition: number = currentYear - 10;

    for (let i = 0; i <= 20; i++) {
      const year: number = startPosition + i;
      const date = new Date(year, 11, 31, 0, 0);
      const intlOptions: Intl.DateTimeFormatOptions = { year: "numeric" };
      const jpYear: string = new Intl.DateTimeFormat("ja-JP-u-ca-japanese", intlOptions).format(date).slice(0, -1); // 年が重複するので最終文字だけ削除;

      options.push({
        text: `${year}（${jpYear}）`,
        value: `${year}`
      });
    }

    return options;
  }

  public getMonthOptions(): Array<{ text: string; value: string | null }> {
    const options: Array<{ text: string; value: string | null }> = [{ text: "", value: null }]; // 初期値にリセット用の選択肢を用意しておく

    for (let i = 1; i <= 12; i++) {
      options.push({
        text: `${i}`,
        value: `${i}`
      });
    }

    return options;
  }

  public getDateOptions(): Array<{ text: string; value: string | null }> {
    const options: Array<{ text: string; value: string | null }> = [{ text: "", value: null }]; // 初期値にリセット用の選択肢を用意しておく

    for (let i = 1; i <= 31; i++) {
      options.push({
        text: `${i}`,
        value: `${i}`
      });
    }

    return options;
  }

  /**
   * ### 待機者ラベルを取得する
   */
  public async getWaiterLabels(): Promise<Array<WaiterLabel>> {
    const response: Array<WaiterLabelsGetModel> = await this.waiterLabelsService.gets(userStore);

    let labels: Array<WaiterLabel> = [];
    for (const label of response) {
      labels.push({
        ...label,
        checked: false
      });
    }

    return labels;
  }

  /**
   * ### 登録確認モーダルを表示する
   */
  public async showConfirmModal(residentStatus: ResidentStatus): Promise<void> {
    if (residentStatus == ResidentStatus.TEMPORARILY) {
      this.residentStatus = residentStatus;

      this.registerModalTitle = "入所申込者の情報を一時保存しますか？";
      this.showRegisterModal = true;
      return;
    }

    const validationResult: boolean = await this.$validator.validateAll();

    // 参照渡しだとエラー有無が上手く設定できないのでvalidInputErrorというクラス内のグローバルプロパティにエラー有無は設定する
    this.validInputError = true;
    if (this.template) {
      await this.validateBirthday(this.template.template.properties);
      await this.validateAdmissionApplicationCreated(this.admissionApplicationCreated);
      await this.validateRoomType(this.template.template.properties);
      await this.validateFile(this.template.template.properties);
    }

    if (validationResult && this.validInputError) {
      this.residentStatus = residentStatus;

      this.registerModalTitle = "入所申込者の情報を登録しますか？";
      this.showValidationAlert = false;
      this.showRegisterModal = true;
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  /**
   * ### 生年月日の存在するかしないかのバリデーション
   *
   * - 生年月日が1つでも入力されていればバリデーション対象として存在確認する
   * - 生年月日が1つも入力されていない場合はバリデーションせず、OKとする
   */
  public async validateBirthday(properties: Array<TemplateItem>): Promise<void> {
    for (const property of properties) {
      if (this.hasProperties(property) == true) {
        if (property.type == TemplateType.SECTION) {
          const item: TemplateItemSection = property as TemplateItemSection;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            await this.validateBirthday(item.properties);
          }
        } else if (property.type == TemplateType.GROUP) {
          const item: TemplateItemGroup = property as TemplateItemGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties)) {
                await this.validateBirthday(childProperties);
              }
            }
          }
        } else if (property.type == TemplateType.RADIO_GROUP) {
          const item: TemplateItemRadioGroup = property as TemplateItemRadioGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties.properties)) {
                await this.validateBirthday(childProperties.properties);
              }
            }
          }
        } else if (property.type == TemplateType.SWITCH_CHECKBOX) {
          const item: TemplateItemSwitchCheckbox = property as TemplateItemSwitchCheckbox;

          // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
          if (item.properties && Array.isArray(item.properties)) {
            await this.validateBirthday(item.properties);
          }
        }
      }

      const inputItem: TemplateItemInput = property as TemplateItemInput;
      if (inputItem.type == TemplateType.BIRTHDAY) {
        const birthdayItem: TemplateItemBirthday = inputItem as TemplateItemBirthday;

        if (birthdayItem.answer.year || birthdayItem.answer.month || birthdayItem.answer.date) {
          const birthday: number = this.joinBirthday(birthdayItem);
          const { valid, errors } = await this.$validator.verify(birthday, "exist_date", { name: birthdayItem.label, bails: false });

          if (!valid) {
            for (const error of errors) {
              this.$validator.errors.add({ field: birthdayItem.id, msg: error });
            }
            this.validInputError = false;
          } else {
            this.$validator.errors.remove(birthdayItem.id);
          }
        }
      }
    }
  }

  public async validateAdmissionApplicationCreated(admissionApplicationCreated: YearMonthDateAnswer): Promise<void> {
    if (admissionApplicationCreated.year || admissionApplicationCreated.month || admissionApplicationCreated.date) {
      const _admissionApplicationCreated: TemplateItemBirthday = {
        answer: admissionApplicationCreated
      } as TemplateItemBirthday;
      const date: number = this.joinBirthday(_admissionApplicationCreated);
      const { valid, errors } = await this.$validator.verify(date, "exist_date", { name: "申込日", bails: false });

      if (!valid) {
        for (const error of errors) {
          this.$validator.errors.add({ field: "created", msg: error });
        }
        this.validInputError = false;
      } else {
        this.$validator.errors.remove("created");
      }
    }
  }

  public hasProperties(item: TemplateItem): boolean {
    switch (item.type) {
      case TemplateType.SECTION:
      case TemplateType.GROUP:
      case TemplateType.RADIO_GROUP:
      case TemplateType.SWITCH_CHECKBOX: {
        return true;
      }
      default: {
        return false;
      }
    }
  }

  public joinBirthday(birthdayItem: TemplateItemBirthday): number {
    let month: string = String(birthdayItem.answer.month);
    if (month.length == 1) {
      month = `0${month}`;
    }

    let date: string = String(birthdayItem.answer.date);
    if (date.length == 1) {
      date = `0${date}`;
    }

    const birthday: number = Number(`${birthdayItem.answer.year}${month}${date}000000000`);

    return birthday;
  }

  /**
   * ### 部屋種別のバリデーション
   */
  public async validateRoomType(properties: Array<TemplateItem>): Promise<void> {
    for (const property of properties) {
      if (this.hasProperties(property) == true) {
        if (property.type == TemplateType.SECTION) {
          const item: TemplateItemSection = property as TemplateItemSection;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            await this.validateRoomType(item.properties);
          }
        } else if (property.type == TemplateType.GROUP) {
          const item: TemplateItemGroup = property as TemplateItemGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties)) {
                await this.validateRoomType(childProperties);
              }
            }
          }
        } else if (property.type == TemplateType.RADIO_GROUP) {
          const item: TemplateItemRadioGroup = property as TemplateItemRadioGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties.properties)) {
                await this.validateRoomType(childProperties.properties);
              }
            }
          }
        } else if (property.type == TemplateType.SWITCH_CHECKBOX) {
          const item: TemplateItemSwitchCheckbox = property as TemplateItemSwitchCheckbox;

          // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
          if (item.properties && Array.isArray(item.properties)) {
            await this.validateRoomType(item.properties);
          }
        }
      }

      const inputItem: TemplateItemInput = property as TemplateItemInput;
      if (inputItem.type == TemplateType.ROOM_TYPE) {
        const roomTypeItem: TemplateItemRoomType = inputItem as TemplateItemRoomType;

        let required: boolean = false;
        for (const validationRule of roomTypeItem.validationRules) {
          if (validationRule == "required") {
            required = true;
            break;
          }
        }

        if (required == true) {
          this.$validator.errors.remove(roomTypeItem.id);

          let isRoomInputTrue: boolean = false;
          let isCustomRoomInputTrue: boolean = false;
          for (const answer of roomTypeItem.answer) {
            isRoomInputTrue =
              answer.recruitmentRoom.multiBed ||
              answer.recruitmentRoom.conventinalSingle ||
              answer.recruitmentRoom.unitTypeSingle ||
              answer.recruitmentRoom.double ||
              answer.recruitmentRoom.single;

            if (answer.customRecruitmentRoom) {
              for (const customRoom of answer.customRecruitmentRoom) {
                if (customRoom.use == true) {
                  isCustomRoomInputTrue = true;
                  break;
                }
              }
            }

            if (isRoomInputTrue == true || isCustomRoomInputTrue == true) {
              break;
            }
          }

          if (isRoomInputTrue == false && isCustomRoomInputTrue == false) {
            this.$validator.errors.add({ field: roomTypeItem.id, msg: `${roomTypeItem.label}は必須入力です` });
            this.validInputError = false;
          } else {
            this.$validator.errors.remove(roomTypeItem.id);
          }
        }
      }
    }
  }

  /**
   * ### ファイルのバリデーション
   *
   * - 10MBサイズ
   * - ファイル種類
   */
  public async validateFile(properties: Array<TemplateItem>): Promise<void> {
    for (const property of properties) {
      if (this.hasProperties(property) == true) {
        if (property.type == TemplateType.SECTION) {
          const item: TemplateItemSection = property as TemplateItemSection;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            await this.validateFile(item.properties);
          }
        } else if (property.type == TemplateType.GROUP) {
          const item: TemplateItemGroup = property as TemplateItemGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties)) {
                await this.validateFile(childProperties);
              }
            }
          }
        } else if (property.type == TemplateType.RADIO_GROUP) {
          const item: TemplateItemRadioGroup = property as TemplateItemRadioGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties.properties)) {
                await this.validateFile(childProperties.properties);
              }
            }
          }
        } else if (property.type == TemplateType.SWITCH_CHECKBOX) {
          const item: TemplateItemSwitchCheckbox = property as TemplateItemSwitchCheckbox;

          // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
          if (item.properties && Array.isArray(item.properties)) {
            await this.validateFile(item.properties);
          }
        }
      }

      const inputItem: TemplateItemInput = property as TemplateItemInput;
      if (inputItem.type == TemplateType.FILE) {
        const fileItem: TemplateItemFile = inputItem as TemplateItemFile;

        if (fileItem.tempAnswer) {
          for (let i = 0; i < fileItem.tempAnswer.length; i++) {
            this.$validator.errors.remove(`${fileItem.id}-${i}`);

            // ファイル種類バリデーション
            if (fileItem.tempAnswer[i].file && fileItem.accept && fileItem.accept.indexOf(fileItem.tempAnswer[i].file.type) < 0) {
              this.$validator.errors.add({ field: `${fileItem.id}-${i}`, msg: `${fileItem.label}のファイル形式が正しくありません` });
              this.validInputError = false;
            }

            // 10MBサイズバリデーション
            if (fileItem.tempAnswer[i].file && 1024 * 1024 * 10 < fileItem.tempAnswer[i].file.size) {
              this.$validator.errors.add({ field: `${fileItem.id}-${i}`, msg: `${fileItem.label}のファイルサイズが10MBを超えているため登録できません` });
              this.validInputError = false;
            }
          }
        }
      }
    }
  }

  /**
   * ### 待機者を登録する
   */
  public async registerWaiter(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "登録中...";

    // 一時間未操作が発生した場合に備えて、入力情報を一時保存する
    this.setScreenInputTemporaryStore();

    // ファイルアップロードするタイミングでファイルが削除（移動含む）、リネームされた場合は失敗するのでここでtry-catchする
    // アップロードに失敗した場合は添付されたファイルを全てリセットしてモーダルで再添付を促す
    try {
      const requestBody: WaitersPostModel = await this.createRequestBody(this.template as AdmissionApplicationTemplatesGetModel);
      await this.waitersService.post(userStore, requestBody);
    } catch (error) {
      this.isBusy = false;

      this.showUploadErrorModal = true;
      const _template = this.template as AdmissionApplicationTemplatesGetModel;
      this.resetFileItems(_template.template.properties);

      return;
    }

    // 登録が完了したので入力情報をリセットする
    this.resetScreenInputTemporaryStore();

    // 待機者一覧を再読み込みさせるようstoreを更新
    let waiterList: WaiterListState = needRefreshesStore.waiterList;
    waiterList.waiterList = true;
    needRefreshesStore.setWaiterList(waiterList);
    // メッセージ送信(待機者)の宛先が変更になるので再読み込みさせるようstoreを更新
    let sendWaiterMessageState: SendWaiterMessagesState = needRefreshesStore.sendWaiterMessage;
    sendWaiterMessageState.sendMessages = true;
    needRefreshesStore.setSendWaiterMessage(sendWaiterMessageState);

    // サイドメニューを更新
    this.$emit("refresh");

    this.successModalTitle = this.residentStatus == ResidentStatus.APPLICATION ? "登録に成功しました。" : "一時保存に成功しました。";
    this.showRegisterSuccessModal = true;

    this.isBusy = false;

    return;
  }

  /**
   * ### 待機者登録用リクエストボディを作成する
   */
  public async createRequestBody(template: AdmissionApplicationTemplatesGetModel): Promise<WaitersPostModel> {
    let fileItems: Array<TemplateItemFile> = [];
    this.searchFileItems(fileItems, template.template.properties);

    // 初期値入力でtempAnswerに1つ空ファイルを格納しているので、実際に入力されたファイルを探す
    const files: Array<{ file: File; id: string }> = [];
    for (const item of fileItems) {
      if (item.tempAnswer != null) {
        for (let i = 0; i < item.tempAnswer.length; i++) {
          if (item.tempAnswer[i] && item.tempAnswer[i].file != null) {
            files.push({ file: item.tempAnswer[i].file, id: item.id + "-" + i });
          }
        }
      }
    }
    if (0 < files.length) {
      const fileMap: Map<string, { fileName: string; fileUrl: string }> = await this.uploadFiles(files);
      this.setFileTemplateToAnswer(fileMap, template.template.properties);
    }

    const _admissionApplicationCreated: TemplateItemBirthday = {
      answer: this.admissionApplicationCreated
    } as TemplateItemBirthday;
    const created: number = this.joinBirthday(_admissionApplicationCreated);

    let labels: Array<number> = [];
    for (const label of this.labels) {
      if (label.checked == true) {
        labels.push(label.id);
      }
    }

    const requestBody: WaitersPostModel = {
      admissionApplication: {
        templateId: template.id,
        information: template.information,
        templateAnswer: template.template
      },
      status: this.residentStatus,
      created: created,
      labels: labels
    };

    return requestBody;
  }

  public searchFileItems(fileItems: Array<TemplateItemFile>, properties: Array<TemplateItem>): void {
    for (const property of properties) {
      if (this.hasProperties(property) == true) {
        if (property.type == TemplateType.SECTION) {
          const item: TemplateItemSection = property as TemplateItemSection;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            this.searchFileItems(fileItems, item.properties);
          }
        } else if (property.type == TemplateType.GROUP) {
          const item: TemplateItemGroup = property as TemplateItemGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties)) {
                this.searchFileItems(fileItems, childProperties);
              }
            }
          }
        } else if (property.type == TemplateType.RADIO_GROUP) {
          const item: TemplateItemRadioGroup = property as TemplateItemRadioGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties.properties)) {
                this.searchFileItems(fileItems, childProperties.properties);
              }
            }
          }
        } else if (property.type == TemplateType.SWITCH_CHECKBOX) {
          const item: TemplateItemSwitchCheckbox = property as TemplateItemSwitchCheckbox;

          // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
          if (item.properties && Array.isArray(item.properties)) {
            this.searchFileItems(fileItems, item.properties);
          }
        }
      }

      const inputItem: TemplateItemInput = property as TemplateItemInput;
      if (inputItem.type == TemplateType.FILE) {
        const fileItem: TemplateItemFile = inputItem as TemplateItemFile;
        if (fileItem.tempAnswer && 0 < fileItem.tempAnswer.length) {
          fileItems.push(fileItem);
        }
      }
    }
  }

  public async uploadFiles(files: Array<{ file: File; id: string }>): Promise<Map<string, { fileName: string; fileUrl: string }>> {
    const fileMap: Map<string, { fileName: string; fileUrl: string }> = new Map<string, { fileName: string; fileUrl: string }>();

    try {
      // プログレス付きモーダルを表示する
      // 進捗はファイルのアプロード状況を反映させるようにする
      this.currentUploadCount = 0;
      this.uploadCount = files.length;
      this.progressMessage = "ファイルアップロード中。\n完了するまでそのままお待ちください。\nアップロード中に別画面に遷移しますと処理がキャンセルされます。";
      this.progressLoading = true;

      const urls: Array<S3LocationResponse> = await this.createUploadUrls(files, S3LocationType.ADMISSION_APPLICATION);
      for (let i = 0; i < urls.length; i++) {
        this.currentUploadCount = i;
        await this.s3UploadsService.uploadFile(urls[i], files[i].file);

        // DBにはファイルではなく、添付ファイルのS3URLを保持するためURL配列を作成する
        const preUrl: string = urls[i].url.endsWith("/") ? urls[i].url : `${urls[i].url}/`;
        const s3Url: string = `${preUrl}${urls[i].fields["key"]}`;

        fileMap.set(files[i].id, {
          fileName: files[i].file.name,
          fileUrl: s3Url
        });
      }
    } finally {
      this.progressLoading = false;
    }

    this.progressLoading = false;

    return fileMap;
  }

  public async createUploadUrls(files: Array<{ file: File; id: string }>, s3LocationType: S3LocationType): Promise<Array<S3LocationResponse>> {
    const extensions: Array<string> = [];

    for (const file of files) {
      switch (file.file.type) {
        case "image/jpeg": {
          extensions.push(".jpeg");
          break;
        }
        case "image/png": {
          extensions.push(".png");
          break;
        }
        case "video/mp4": {
          extensions.push(".mp4");
          break;
        }
        case "video/quicktime": {
          extensions.push(".mov");
          break;
        }
        case "application/pdf": {
          extensions.push(".pdf");
          break;
        }
      }
    }

    const requestBody: S3LocationRequest = {
      type: s3LocationType,
      count: files.length,
      extensions: extensions,
      params: {
        userId: userStore.loginUser.userId
      },
      acl: "public-read"
    };
    const url: Array<S3LocationResponse> = await this.s3LocationsService.post(userStore, requestBody);

    return url;
  }

  resetFileItems(properties: Array<TemplateItem>): void {
    for (const property of properties) {
      if (this.hasProperties(property) == true) {
        if (property.type == TemplateType.SECTION) {
          const item: TemplateItemSection = property as TemplateItemSection;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            this.resetFileItems(item.properties);
          }
        } else if (property.type == TemplateType.GROUP) {
          const item: TemplateItemGroup = property as TemplateItemGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties)) {
                this.resetFileItems(childProperties);
              }
            }
          }
        } else if (property.type == TemplateType.RADIO_GROUP) {
          const item: TemplateItemRadioGroup = property as TemplateItemRadioGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties.properties)) {
                this.resetFileItems(childProperties.properties);
              }
            }
          }
        } else if (property.type == TemplateType.SWITCH_CHECKBOX) {
          const item: TemplateItemSwitchCheckbox = property as TemplateItemSwitchCheckbox;

          // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
          if (item.properties && Array.isArray(item.properties)) {
            this.resetFileItems(item.properties);
          }
        }
      }

      const inputItem: TemplateItemInput = property as TemplateItemInput;
      if (inputItem.type == TemplateType.FILE) {
        const fileItem: TemplateItemFile = inputItem as TemplateItemFile;
        fileItem.answer = [];
        fileItem.tempAnswer = [];
      }
    }
    return;
  }

  public setFileTemplateToAnswer(fileMap: Map<string, { fileName: string; fileUrl: string }>, properties: Array<TemplateItem>): void {
    for (const property of properties) {
      if (this.hasProperties(property) == true) {
        if (property.type == TemplateType.SECTION) {
          const item: TemplateItemSection = property as TemplateItemSection;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            this.setFileTemplateToAnswer(fileMap, item.properties);
          }
        } else if (property.type == TemplateType.GROUP) {
          const item: TemplateItemGroup = property as TemplateItemGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties)) {
                this.setFileTemplateToAnswer(fileMap, childProperties);
              }
            }
          }
        } else if (property.type == TemplateType.RADIO_GROUP) {
          const item: TemplateItemRadioGroup = property as TemplateItemRadioGroup;

          if (item.properties && Array.isArray(item.properties)) {
            // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
            for (const childProperties of item.properties) {
              if (childProperties && Array.isArray(childProperties.properties)) {
                this.setFileTemplateToAnswer(fileMap, childProperties.properties);
              }
            }
          }
        } else if (property.type == TemplateType.SWITCH_CHECKBOX) {
          const item: TemplateItemSwitchCheckbox = property as TemplateItemSwitchCheckbox;

          // LIFF側でpropertiesをNULLにしてしまう場合があるため、チェックする
          if (item.properties && Array.isArray(item.properties)) {
            this.setFileTemplateToAnswer(fileMap, item.properties);
          }
        }
      }

      const inputItem: TemplateItemInput = property as TemplateItemInput;
      if (inputItem.type == TemplateType.FILE) {
        const fileItem: TemplateItemFile = inputItem as TemplateItemFile;

        if (fileItem.answer == null) {
          fileItem.answer = [];
        }

        if (fileItem.tempAnswer != null) {
          for (let i = 0; i < fileItem.tempAnswer.length; i++) {
            const s3File: { fileName: string; fileUrl: string } | undefined = fileMap.get(fileItem.id + "-" + i);
            if (s3File != undefined) {
              fileItem.answer.push({
                url: s3File.fileUrl,
                name: s3File.fileName
              });
            }
          }
        }

        fileItem.tempAnswer = [];
      }
    }
    return;
  }

  public async onAddWaiter(): Promise<void> {
    const url: string = await this.getRegisterWaiterUrl();
    window.open(url, "_blank");
    return;
  }

  public async getRegisterWaiterUrl(): Promise<string> {
    const response: string = await this.waitersService.getWaiterRegisterUrl(userStore);
    return response;
  }

  /**
   * ### 待機者一覧に遷移する
   */
  public gotoWaiterList(): void {
    this.$router.push(PcUrl.WaiterList);
    return;
  }

  /**
   * ### 画面入力情報をstoreから復元し、store情報を初期化する
   */
  public restoreScreenInputTemporaryStore(): void {
    if (screenInputTemporaryStore.url == PcUrl.WaiterRegister) {
      this.template = screenInputTemporaryStore.inputParams.template;
      this.admissionApplicationCreated = screenInputTemporaryStore.inputParams.admissionApplicationCreated;

      this.resetScreenInputTemporaryStore();
    }
    return;
  }

  /**
   * ### 画面入力情報のstoreをリセットする
   */
  public resetScreenInputTemporaryStore(): void {
    screenInputTemporaryStore.initializeScreenInputTemporary();
    return;
  }

  /**
   * ### 画面入力情報をstoreに格納する
   *
   * - 一時間無操作タイムアウト対策としてAPIリクエスト前にstoreに保存する
   */
  public setScreenInputTemporaryStore(): void {
    screenInputTemporaryStore.setScreenInputTemporary({
      url: PcUrl.WaiterRegister,
      inputParams: {
        template: this.template,
        admissionApplicationCreated: this.admissionApplicationCreated
      }
    });
    return;
  }
}
interface WaiterLabel extends WaiterLabelsGetModel {
  /** チェック */
  checked: boolean;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

#waiter-register {
  max-width: 840px;
  margin-left: auto;
  margin-right: auto;

  .box-header {
    .title {
      font-size: 22px;
      font-weight: bold;
    }
    .add-btn {
      width: 176px;
    }
  }

  .created,
  .labels {
    .title {
      font-weight: bold;
    }
  }

  .created {
    .year {
      width: 180px;
    }
    .month {
      width: 70px;
    }
    .date {
      width: 70px;
    }

    .validation-label {
      color: $validation-label-font-color;
      font-size: 10px;
      border: 1px solid $validation-label-font-color;
      padding: 1px 4px 0 4px;
      // 「必須」の文字が少し下にズレるでの、marginで調整する
      margin: 2px 6px 4px 0;
    }
    .noinput {
      background-color: $bg-require-input !important;
      &::placeholder {
        color: rgb(117, 117, 117);
      }
    }
  }

  .labels {
    .waiter-label {
      margin: 2px 0;
      font-size: 12px;
      padding: 4px;
      border-radius: 0.25rem;
    }
  }

  .register-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
